import { applyTheme, DEFAULT_THEME, normalizeThemeColor } from './theme';

export const COMPANY_STORAGE_KEY = 'activeCompany';
export const COMPANY_CACHE_KEY = 'accessibleCompanies';
export const COMPANY_CACHE_TIMESTAMP_KEY = 'accessibleCompaniesCachedAt';

const parseJson = (value) => {
  try {
    return JSON.parse(value);
  } catch (error) {
    console.error('Failed to parse company storage value', error);
    return null;
  }
};

export const broadcastCompanyChange = (company) => {
  if (typeof window !== 'undefined' && typeof window.dispatchEvent === 'function') {
    window.dispatchEvent(new CustomEvent('activeCompanyChanged', { detail: company ?? null }));
  }
};

const normalizeCompanyPayload = (company) => {
  if (!company || !company.id) {
    return null;
  }

  return {
    id: company.id,
    name: company.name ?? company.company_name ?? '',
    code: company.code ?? company.company_code ?? company.slug ?? null,
    themeColor: normalizeThemeColor(company.themeColor ?? company.theme_color),
  };
};

export const getActiveCompany = () => {
  if (typeof window === 'undefined') {
    return null;
  }

  const stored = localStorage.getItem(COMPANY_STORAGE_KEY);
  if (!stored) {
    return null;
  }

  const parsed = parseJson(stored);
  return normalizeCompanyPayload(parsed);
};

export const getActiveCompanyId = () => getActiveCompany()?.id ?? null;

export const setActiveCompany = (company) => {
  if (!company || !company.id) {
    localStorage.removeItem(COMPANY_STORAGE_KEY);
    applyTheme(DEFAULT_THEME);
    broadcastCompanyChange(null);
    return;
  }

  const normalized = normalizeCompanyPayload(company);
  if (!normalized) {
    localStorage.removeItem(COMPANY_STORAGE_KEY);
    applyTheme(DEFAULT_THEME);
    broadcastCompanyChange(null);
    return;
  }

  localStorage.setItem(COMPANY_STORAGE_KEY, JSON.stringify(normalized));
  applyTheme(normalized.themeColor);
  broadcastCompanyChange(normalized);
};

export const clearActiveCompany = () => {
  localStorage.removeItem(COMPANY_STORAGE_KEY);
  applyTheme(DEFAULT_THEME);
  broadcastCompanyChange(null);
};

export const cacheAccessibleCompanies = (companies) => {
  if (!Array.isArray(companies)) {
    localStorage.removeItem(COMPANY_CACHE_KEY);
    localStorage.removeItem(COMPANY_CACHE_TIMESTAMP_KEY);
    return;
  }

  const normalizedCompanies = companies
    .map((company) => normalizeCompanyPayload(company))
    .filter(Boolean);

  localStorage.setItem(COMPANY_CACHE_KEY, JSON.stringify(normalizedCompanies));
  localStorage.setItem(COMPANY_CACHE_TIMESTAMP_KEY, String(Date.now()));
};

export const getCachedCompanies = () => {
  if (typeof window === 'undefined') {
    return [];
  }

  const stored = localStorage.getItem(COMPANY_CACHE_KEY);
  if (!stored) {
    return [];
  }

  const parsed = parseJson(stored);
  if (!Array.isArray(parsed)) {
    return [];
  }

  return parsed
    .map((company) => normalizeCompanyPayload(company))
    .filter(Boolean);
};

export const getCachedCompaniesAge = () => {
  if (typeof window === 'undefined') {
    return Number.POSITIVE_INFINITY;
  }

  const cachedAt = Number(localStorage.getItem(COMPANY_CACHE_TIMESTAMP_KEY));
  return Number.isFinite(cachedAt) && cachedAt > 0
    ? Date.now() - cachedAt
    : Number.POSITIVE_INFINITY;
};

export const clearCompanyCache = () => {
  localStorage.removeItem(COMPANY_CACHE_KEY);
  localStorage.removeItem(COMPANY_CACHE_TIMESTAMP_KEY);
};

export const applyActiveCompanyTheme = () => {
  const activeCompany = getActiveCompany();
  applyTheme(activeCompany?.themeColor ?? DEFAULT_THEME);
};
